import {isNotNil} from 'ramda';

import type {EagColumn} from '../types/EagColumn';
import type {RowData} from '../types/RowData';
import type {SelectedCellRange} from '../types/SelectedCellRange';

export type ClipboardCopyTarget = {
  rows: RowData[];
  columns: EagColumn[];
};

export type ResolveClipboardCopyTargetParams = {
  /** Row the context menu was opened on. */
  clickedRow: RowData;
  /** Current-page rows in render order. */
  rows: RowData[];
  /** Visible columns in display order. */
  columns: EagColumn[];
  /** Committed drag-selected cell range, null when none. */
  cellRange: SelectedCellRange | null;
  /** `data-column` of the right-clicked cell, null when the click landed outside a data cell. */
  clickedColumnKey: string | null;
  /** `aria-rowindex` of the right-clicked row, null when unavailable. */
  clickedRowIndex: number | null;
};

const spansMultipleCells = (range: SelectedCellRange): boolean =>
  range.columnKeys.length > 1 || range.minRow < range.maxRow;

const containsClickedCell = (
  range: SelectedCellRange,
  columnKey: string | null,
  rowIndex: number | null
): boolean =>
  isNotNil(columnKey) &&
  isNotNil(rowIndex) &&
  range.columnKeys.includes(columnKey) &&
  rowIndex >= range.minRow &&
  rowIndex <= range.maxRow;

/**
 * Decides which rows × columns a context-menu copy serializes, by precedence:
 * 1. Drag-selected cell range — when a multi-cell range exists and the right-click
 *    landed inside it (a single-cell range defers to the plain cell behavior below).
 * 2. Clicked cell — the clicked row scoped to the clicked column (AG Grid parity:
 *    built-in copy acts on the focused cell). Clicks outside a data cell
 *    (checkbox / actions / filler td) fall back to all columns.
 *
 * Checkbox row selection deliberately does NOT widen the scope: copying must act
 * on the cell under the cursor even when the clicked row is part of a selection
 * (T20-137183 — selection-wide copy surprised users).
 */
export function resolveClipboardCopyTarget(
  params: ResolveClipboardCopyTargetParams
): ClipboardCopyTarget {
  const {clickedRow, rows, columns, cellRange, clickedColumnKey, clickedRowIndex} = params;

  const activeRange =
    isNotNil(cellRange) &&
    spansMultipleCells(cellRange) &&
    containsClickedCell(cellRange, clickedColumnKey, clickedRowIndex)
      ? cellRange
      : null;

  if (isNotNil(activeRange)) {
    const rangeColumnKeys = new Set(activeRange.columnKeys);
    return {
      rows: rows.slice(activeRange.minRow, activeRange.maxRow + 1),
      columns: columns.filter((column) => rangeColumnKeys.has(column.key)),
    };
  }

  const clickedColumn = columns.find((column) => column.key === clickedColumnKey);
  return {
    rows: [clickedRow],
    columns: isNotNil(clickedColumn) ? [clickedColumn] : columns,
  };
}
